import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Crown, Receipt } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { ExitSurveyModal } from "@/components/ExitSurveyModal";

const BillingPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [billingData, setBillingData] = useState({ invoices: [], subscription: null });
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => { fetchBillingHistory(); }, []);

  const fetchBillingHistory = async () => {
    try {
      const response = await fetch(`${API}/payments/billing-history`, { credentials: "include" });
      if (response.ok) { const data = await response.json(); setBillingData(data); }
    } catch (error) { toast.error("Failed to load billing history"); }
    finally { setLoading(false); }
  };

  const handleCancelSubscription = async (immediately = false) => {
    setCancelling(true);
    try {
      const response = await fetch(`${API}/payments/cancel-subscription`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ cancel_immediately: immediately }) });
      const data = await response.json();
      if (response.ok) { toast.success(data.message); setShowCancelModal(false); fetchBillingHistory(); checkAuth(); }
      else { toast.error(data.detail || "Failed to cancel subscription"); }
    } catch (error) { toast.error("Failed to cancel subscription"); }
    finally { setCancelling(false); }
  };

  const handleReactivate = async () => {
    try {
      const response = await fetch(`${API}/payments/reactivate-subscription`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" });
      const data = await response.json();
      if (response.ok) { toast.success(data.message); fetchBillingHistory(); checkAuth(); }
      else { toast.error(data.detail || "Failed to reactivate subscription"); }
    } catch (error) { toast.error("Failed to reactivate subscription"); }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-text-secondary hover:text-white mb-4 transition-colors">
              <ChevronLeft className="w-5 h-5" /> Back to Dashboard
            </button>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">BILLING & <span className="gradient-text">SUBSCRIPTION</span></h1>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-surface border border-border rounded-xl p-6 mb-6">
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-primary" /> Current Subscription</h2>
                {billingData.subscription ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-2xl font-bold capitalize">{user?.subscription_tier || billingData.subscription.plan_name}</p>
                        <p className="text-text-secondary">{formatCurrency(billingData.subscription.plan_amount)} / {billingData.subscription.plan_interval}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${billingData.subscription.status === 'active' ? 'bg-green-500/20 text-green-400' : billingData.subscription.status === 'canceled' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {billingData.subscription.cancel_at_period_end ? 'Cancels Soon' : billingData.subscription.status.charAt(0).toUpperCase() + billingData.subscription.status.slice(1)}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                      <div><p className="text-text-muted text-sm">Current Period</p><p className="text-text-secondary">{formatDate(billingData.subscription.current_period_start)} - {formatDate(billingData.subscription.current_period_end)}</p></div>
                      <div><p className="text-text-muted text-sm">Next Billing Date</p><p className="text-text-secondary">{billingData.subscription.cancel_at_period_end ? <span className="text-red-400">Subscription ends {formatDate(billingData.subscription.current_period_end)}</span> : formatDate(billingData.subscription.current_period_end)}</p></div>
                    </div>
                    <div className="flex flex-wrap gap-3 pt-4">
                      {billingData.subscription.cancel_at_period_end ? (
                        <button onClick={handleReactivate} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors">Reactivate Subscription</button>
                      ) : (
                        <button onClick={() => setShowCancelModal(true)} className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-colors" data-testid="cancel-subscription-btn">Cancel Subscription</button>
                      )}
                      <button onClick={() => navigate("/pricing")} className="px-4 py-2 bg-surface border border-border rounded-lg hover:border-primary/50 transition-colors">Change Plan</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-text-secondary mb-4">No active subscription</p>
                    <button onClick={() => navigate("/pricing")} className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors">View Plans</button>
                  </div>
                )}
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-surface border border-border rounded-xl p-6">
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><Receipt className="w-5 h-5 text-primary" /> Billing History</h2>
                {billingData.invoices.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead><tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-text-muted text-sm font-medium">Date</th>
                        <th className="text-left py-3 px-2 text-text-muted text-sm font-medium">Description</th>
                        <th className="text-left py-3 px-2 text-text-muted text-sm font-medium">Amount</th>
                        <th className="text-left py-3 px-2 text-text-muted text-sm font-medium">Status</th>
                        <th className="text-right py-3 px-2 text-text-muted text-sm font-medium">Invoice</th>
                      </tr></thead>
                      <tbody>
                        {billingData.invoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b border-border/50 hover:bg-white/5">
                            <td className="py-3 px-2 text-text-secondary">{formatDate(invoice.date)}</td>
                            <td className="py-3 px-2">{invoice.description}</td>
                            <td className="py-3 px-2 font-medium">{formatCurrency(invoice.amount)}</td>
                            <td className="py-3 px-2"><span className={`px-2 py-1 rounded text-xs font-medium ${invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}</span></td>
                            <td className="py-3 px-2 text-right">{invoice.invoice_pdf && (<a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">Download</a>)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8"><Receipt className="w-12 h-12 text-text-muted mx-auto mb-4" /><p className="text-text-secondary">No billing history yet</p></div>
                )}
              </motion.div>
            </>
          )}
        </div>
      </div>

      {showCancelModal && (
        <ExitSurveyModal
          open={showCancelModal}
          onClose={() => !cancelling && setShowCancelModal(false)}
          onConfirmCancel={() => handleCancelSubscription(false)}
          cancelling={cancelling}
        />
      )}
    </div>
  );
};

export default BillingPage;
